import { getRuntimeConfig } from "@/config";
import type { PublicUnitDto } from "./types";

/**
 * Error classes for the customer portal API client. Three concrete shapes the
 * UI cares about: not-found (show "try again"), rate-limited (show countdown
 * with the Retry-After value), generic network/server (show retry).
 */
export class NotFoundError extends Error {
  constructor() {
    super("not-found");
    this.name = "NotFoundError";
  }
}

export class RateLimitedError extends Error {
  /** Seconds the server asked us to wait. Parsed from `Retry-After`. */
  readonly retryAfterSeconds: number;
  constructor(retryAfterSeconds: number) {
    super("rate-limited");
    this.name = "RateLimitedError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Builds the full URL for a public-portal API call. The base comes from
 * `/config.json` at runtime (see @/config) — main.tsx resolves it before the
 * first render, so it is always available by the time a query runs.
 * Exported for tests.
 */
export function buildUrl(path: string): string {
  if (!path.startsWith("/")) {
    throw new Error(`API path must start with '/' (got '${path}')`);
  }
  return `${getRuntimeConfig().apiBaseUrl}${path}`;
}

/**
 * Performs a GET against the public-portal API and maps the response to the
 * three error shapes above (or returns the parsed body on 200). Any other
 * non-2xx → generic Error so the UI's error boundary shows the "something
 * went wrong" message.
 */
export async function getPublicUnit(
  path: string,
  signal?: AbortSignal,
): Promise<PublicUnitDto> {
  const response = await fetch(buildUrl(path), {
    method: "GET",
    headers: { Accept: "application/json" },
    // No credentials — anonymous endpoint, no cookies, no Authorization.
    credentials: "omit",
    signal,
  });

  if (response.status === 404) {
    throw new NotFoundError();
  }
  if (response.status === 429) {
    throw new RateLimitedError(parseRetryAfter(response.headers.get("Retry-After")));
  }
  if (!response.ok) {
    throw new Error(`Public API error ${response.status}`);
  }
  return (await response.json()) as PublicUnitDto;
}

/**
 * Parses an RFC-7231 Retry-After value. Two formats are valid:
 *   * Integer seconds → return as-is.
 *   * HTTP-date → diff against now.
 * Falls back to 5 seconds when the header is missing or unparseable so the
 * UI countdown always has a non-zero value to show.
 */
export function parseRetryAfter(header: string | null): number {
  if (!header) return 5;
  // Treat pure-numeric input as "delta seconds" per RFC 7231 — including
  // edge cases like "0" or negative values, which we deliberately ignore
  // (UI needs a non-zero countdown to render something useful).
  if (/^-?\d+$/.test(header.trim())) {
    const asInt = Number.parseInt(header, 10);
    return asInt > 0 ? asInt : 5;
  }
  const asDate = Date.parse(header);
  if (!Number.isNaN(asDate)) {
    return Math.max(1, Math.ceil((asDate - Date.now()) / 1000));
  }
  return 5;
}
